import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { normalizeImageUrl } from "@/utils/imageUtils";
import { 
  Plus, 
  Edit, 
  Package, 
  Image, 
  Eye, 
  EyeOff, 
  Star, 
  Upload,
  ExternalLink,
  Store
} from "lucide-react";

export default function AdminProductos() {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    nombre: "",
    descripcion: "",
    precio: "",
    activo: true,
    imagen_url: "",
    visible_catalogo: true,
    categoria_catalogo: "Productos",
    orden_catalogo: 99,
    destacado_catalogo: false,
  });

  useEffect(() => {
    fetchProductos();
  }, []);

  const fetchProductos = async () => {
    try {
      const response = await api.get("/productos");
      setProductos(response.data);
    } catch (error) {
      toast.error("Error al cargar productos");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Normalizar URL de imagen antes de enviar
      const normalizedImageUrl = normalizeImageUrl(form.imagen_url);
      
      const data = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        precio: parseFloat(form.precio),
        activo: form.activo,
        imagen_url: normalizedImageUrl,
        visible_catalogo: form.visible_catalogo,
        categoria_catalogo: form.categoria_catalogo || "Productos",
        orden_catalogo: parseInt(form.orden_catalogo) || 99,
        destacado_catalogo: form.destacado_catalogo,
      };

      if (editingId) {
        await api.put(`/productos/${editingId}`, data);
        toast.success("Producto actualizado");
      } else {
        await api.post("/productos", data);
        toast.success("Producto creado");
      }
      setDialogOpen(false);
      resetForm();
      fetchProductos();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEdit = (producto) => {
    setEditingId(producto.id);
    setForm({
      nombre: producto.nombre,
      descripcion: producto.descripcion,
      precio: producto.precio.toString(),
      activo: producto.activo,
      imagen_url: producto.imagen_url || "",
      visible_catalogo: producto.visible_catalogo !== false,
      categoria_catalogo: producto.categoria_catalogo || "Productos",
      orden_catalogo: producto.orden_catalogo || 99,
      destacado_catalogo: producto.destacado_catalogo || false,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({
      nombre: "",
      descripcion: "",
      precio: "",
      activo: true,
      imagen_url: "",
      visible_catalogo: true,
      categoria_catalogo: "Productos",
      orden_catalogo: 99,
      destacado_catalogo: false,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value);
  };

  // Categorías predefinidas
  const categorias = ["Productos", "Tortas", "Combos", "Especiales", "Bebidas", "Otros"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-torta-brown">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-productos">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Productos</h1>
          <p className="text-torta-muted">Gestiona tu catálogo de tortas</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-torta-brown hover:bg-torta-brown/90">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Producto
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {editingId ? "Editar Producto" : "Nuevo Producto"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              {/* Información básica */}
              <div className="space-y-4">
                <h3 className="font-medium text-torta-dark text-sm flex items-center gap-2">
                  <Package className="w-4 h-4 text-torta-brown" />
                  Información Básica
                </h3>
                
                <div className="space-y-2">
                  <Label>Nombre</Label>
                  <Input
                    required
                    value={form.nombre}
                    onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                    placeholder="Ej: Torta Familiar"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea
                    required
                    value={form.descripcion}
                    onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                    placeholder="Descripción del producto..."
                    rows={2}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Precio (COP)</Label>
                  <Input
                    type="number"
                    required
                    value={form.precio}
                    onChange={(e) => setForm({ ...form, precio: e.target.value })}
                    placeholder="6000"
                  />
                </div>
                
                <div className="flex items-center justify-between bg-torta-cream/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Producto activo</Label>
                  </div>
                  <Switch
                    checked={form.activo}
                    onCheckedChange={(checked) => setForm({ ...form, activo: checked })}
                  />
                </div>
              </div>

              {/* Configuración del Catálogo */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="font-medium text-torta-dark text-sm flex items-center gap-2">
                  <Store className="w-4 h-4 text-torta-brown" />
                  Configuración del Catálogo Digital
                </h3>
                
                {/* Imagen */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Image className="w-4 h-4 text-torta-muted" />
                    Foto de Portada (URL)
                  </Label>
                  <Input
                    type="url"
                    value={form.imagen_url}
                    onChange={(e) => setForm({ ...form, imagen_url: e.target.value })}
                    placeholder="https://i.imgur.com/XXXXX.jpg"
                  />
                  {form.imagen_url && (
                    <div className="mt-2 relative">
                      <img 
                        src={normalizeImageUrl(form.imagen_url) || form.imagen_url} 
                        alt="Vista previa" 
                        className="w-full h-32 object-cover rounded-lg border"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                        onLoad={(e) => {
                          e.target.style.display = 'block';
                          if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                        }}
                      />
                      <div className="hidden w-full h-32 bg-red-50 border border-red-200 rounded-lg items-center justify-center text-red-600 text-sm">
                        <span>Error al cargar imagen. Verifica la URL.</span>
                      </div>
                      <a 
                        href={normalizeImageUrl(form.imagen_url) || form.imagen_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="absolute top-2 right-2 bg-white/80 p-1 rounded-full hover:bg-white"
                      >
                        <ExternalLink className="w-4 h-4 text-torta-brown" />
                      </a>
                    </div>
                  )}
                  <div className="text-xs text-torta-muted space-y-1">
                    <p>Para <strong>Imgur</strong>: Usa la URL directa (ej: <code className="bg-torta-cream px-1 rounded">https://i.imgur.com/XXXXX.jpg</code>)</p>
                    <p className="text-orange-600">⚠️ No uses imgur.com/XXXXX, usa <strong>i.</strong>imgur.com/XXXXX<strong>.jpg</strong></p>
                  </div>
                </div>

                {/* Visible en catálogo */}
                <div className="flex items-center justify-between bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    {form.visible_catalogo ? (
                      <Eye className="w-4 h-4 text-blue-600" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                    <div>
                      <Label className="text-sm">Mostrar en Catálogo</Label>
                      <p className="text-xs text-torta-muted">
                        {form.visible_catalogo ? "Visible para clientes" : "Oculto del catálogo público"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={form.visible_catalogo}
                    onCheckedChange={(checked) => setForm({ ...form, visible_catalogo: checked })}
                  />
                </div>

                {/* Categoría */}
                <div className="space-y-2">
                  <Label>Categoría en Catálogo</Label>
                  <Select 
                    value={form.categoria_catalogo} 
                    onValueChange={(val) => setForm({ ...form, categoria_catalogo: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar categoría" />
                    </SelectTrigger>
                    <SelectContent>
                      {categorias.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Orden */}
                <div className="space-y-2">
                  <Label>Orden de aparición</Label>
                  <Input
                    type="number"
                    min="1"
                    max="99"
                    value={form.orden_catalogo}
                    onChange={(e) => setForm({ ...form, orden_catalogo: e.target.value })}
                    placeholder="1-99 (menor = aparece primero)"
                  />
                  <p className="text-xs text-torta-muted">
                    Productos con menor número aparecen primero
                  </p>
                </div>

                {/* Destacado */}
                <div className="flex items-center justify-between bg-yellow-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Star className={`w-4 h-4 ${form.destacado_catalogo ? "text-yellow-500 fill-yellow-500" : "text-gray-400"}`} />
                    <div>
                      <Label className="text-sm">Producto Destacado</Label>
                      <p className="text-xs text-torta-muted">
                        Muestra una insignia especial en el catálogo
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={form.destacado_catalogo}
                    onCheckedChange={(checked) => setForm({ ...form, destacado_catalogo: checked })}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancelar
                </Button>
                <Button type="submit" className="bg-torta-brown hover:bg-torta-brown/90">
                  {editingId ? "Guardar Cambios" : "Crear Producto"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Products Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {productos.map((p) => (
          <div
            key={p.id}
            className={`bg-white rounded-2xl overflow-hidden shadow-sm border border-torta-sand/50 ${
              !p.activo ? "opacity-60" : ""
            }`}
            data-testid={`product-card-${p.id}`}
          >
            {/* Imagen de portada */}
            <div className="aspect-video bg-torta-cream relative">
              {p.imagen_url ? (
                <img 
                  src={normalizeImageUrl(p.imagen_url) || p.imagen_url} 
                  alt={p.nombre}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div 
                className={`absolute inset-0 flex items-center justify-center ${p.imagen_url ? 'hidden' : 'flex'}`}
              >
                <Package className="w-12 h-12 text-torta-brown/30" />
              </div>
              
              {/* Badges */}
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                {p.destacado_catalogo && (
                  <span className="bg-yellow-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <Star className="w-3 h-3 fill-white" /> Destacado
                  </span>
                )}
                {p.visible_catalogo === false && (
                  <span className="bg-gray-500 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <EyeOff className="w-3 h-3" /> Oculto
                  </span>
                )}
              </div>
              
              {/* Edit button */}
              <Button 
                size="sm" 
                variant="secondary" 
                className="absolute top-2 right-2 h-8 w-8 p-0 bg-white/80 hover:bg-white"
                onClick={() => handleEdit(p)}
              >
                <Edit className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Info */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-display text-lg text-torta-dark">
                    {p.nombre}
                  </h3>
                  {p.categoria_catalogo && p.categoria_catalogo !== "Productos" && (
                    <span className="text-xs text-torta-muted bg-torta-cream px-2 py-0.5 rounded-full">
                      {p.categoria_catalogo}
                    </span>
                  )}
                </div>
              </div>
              
              <p className="text-torta-muted text-sm mb-3 line-clamp-2">
                {p.descripcion}
              </p>
              
              <div className="flex items-center justify-between">
                <span className="font-display text-xl text-torta-brown">
                  {formatCurrency(p.precio)}
                </span>
                <div className="flex items-center gap-2">
                  {p.visible_catalogo !== false && (
                    <span className="text-xs text-blue-600 flex items-center gap-1">
                      <Eye className="w-3 h-3" /> Catálogo
                    </span>
                  )}
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      p.activo
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {p.activo ? "Activo" : "Inactivo"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {productos.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl">
          <Package className="w-12 h-12 text-torta-muted mx-auto mb-4" />
          <p className="text-torta-muted">No hay productos registrados</p>
        </div>
      )}
    </div>
  );
}
